/**
 * The draft catalogue for the preview. First choice: the brand's preview feed (POST
 * /admin/v1/preview-token, then GET /v1/feed?preview=<token>), exactly what the apps would get.
 * When the browser may not read it (the feed route sends no CORS headers for the console's
 * origin), the same catalogue is put together from the console's own list of items: everything
 * shown in the app or saved as a draft, minus what the preview leaves out (a title without its
 * video). `fromEntities` is pure and unit-tested.
 */

import type { FeedLike, FeedItem, FeedCollection } from "./layout";

export interface EntityLike { type: string; id: string; status: string; data: Record<string, unknown> }

export function fromEntities(entities: EntityLike[]): { feed: FeedLike; leftOut: string[] } {
  const shown = entities.filter((e) => e.status !== "archived");
  const of = <T,>(type: string) => shown.filter((e) => e.type === type).map((e) => e.data as unknown as T);
  const leftOut: string[] = [];
  const titles = of<FeedItem & { playback?: { video?: { videoId?: string } } }>("title").filter((t) => {
    const ok = !!t.playback?.video?.videoId && !!t.durationSeconds;
    if (!ok) leftOut.push(t.id);
    return ok;
  });
  const titleIds = new Set(titles.map((t) => t.id));
  const series = of<FeedItem>("series").map((s) => ({ ...s, seasons: (s.seasons ?? []).map((p) => ({ ...p, titleIds: p.titleIds.filter((id) => titleIds.has(id)) })) }));
  return {
    feed: {
      pages: of<FeedLike["pages"][number]>("page"),
      collections: of<FeedCollection>("collection"),
      titles,
      series,
      liveEvents: of<FeedItem>("liveEvent"),
      taxonomies: of<NonNullable<FeedLike["taxonomies"]>[number]>("taxonomy"),
      stages: of<NonNullable<FeedLike["stages"]>[number]>("stage"),
    },
    leftOut,
  };
}
